import { Stack } from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormChipGroup } from 'src/components/Forms/FormChipGroup/form-chip-group';
import { FormDatePicker } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { Address, useOfferedProducts } from 'src/graphql';
import {
  COMPLIMENTARY_PERIOD_SECTION,
  ComplimentaryPeriodSectionField,
  CUSTOM_LABEL,
  DEFAULT_CPP_LENGTH_DAYS,
  getCppLengthList,
  getCppOptionFromList,
  MAX_DATE,
  TODAY_DATE,
  TOMORROW_DATE,
} from './const';

// literal keys: react-hook-form cannot resolve a nested path through enum-typed keys
export interface AddComplimentaryPeriodFormValues {
  partnerProductId: string;
  cppExpirationDate: Dayjs | null;
}

export interface ComplimentaryPeriodSectionValues {
  [COMPLIMENTARY_PERIOD_SECTION]: AddComplimentaryPeriodFormValues;
}

interface AddComplimentaryPeriodFormProps {
  address?: Partial<Address> | null;
  // When used in submit day1, this value is gonna be used as the minimum date for the expiration date
  baseDate?: Dayjs | null;
}

function isDateWithinAllowedBoundariesForCPP(date: Dayjs | null) {
  if (!date) return false;

  return !date.isBefore(TOMORROW_DATE) && !date.isAfter(MAX_DATE);
}

export const AddComplimentaryPeriodForm = ({
  address,
  baseDate,
}: AddComplimentaryPeriodFormProps) => {
  const { control, setValue } = useFormContext<ComplimentaryPeriodSectionValues>();
  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });
  const sortedOfferedProducts = useMemo(
    () => offeredProducts?.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );
  const productOptions = useMemo(
    () =>
      (sortedOfferedProducts ?? []).map((product) => ({
        id: product.partnerProductId,
        value: product.partnerProductId,
        label: formatMembershipName(product.displayName),
        testId: `complimentary-period-product-option-${product.partnerProductId}`,
      })),
    [sortedOfferedProducts],
  );

  const cppLengthList = useMemo(
    () => getCppLengthList(baseDate?.toDate() || TODAY_DATE),
    [baseDate],
  );

  const DEFAULT_CPP_LENGTH = useMemo(
    () => getCppOptionFromList(cppLengthList, DEFAULT_CPP_LENGTH_DAYS)!,
    [cppLengthList],
  );

  const lengthOptions = useMemo(
    () =>
      cppLengthList.map((item) => ({
        id: item.label,
        value: item.daysAmount,
        label: item.label,
        testId: `complimentary-period-length-${item.daysAmount}`,
      })),
    [cppLengthList],
  );

  const expirationDate = useWatch({
    control,
    name: ComplimentaryPeriodSectionField.CppExpirationDate,
  });
  const selectedPreset = cppLengthList.find(
    (item) =>
      item.label !== CUSTOM_LABEL &&
      !!expirationDate &&
      item.value.isSame(expirationDate, 'day'),
  );
  // tied to the list, so a new Submit Day 1 base date drops the custom mode without an effect
  const [customModeList, setCustomModeList] = useState<typeof cppLengthList | null>(null);
  const isCustomDate = customModeList === cppLengthList || !selectedPreset;
  const customLength = cppLengthList.find((item) => item.label === CUSTOM_LABEL);
  const selectedLength =
    isCustomDate || !selectedPreset ? customLength?.daysAmount : selectedPreset.daysAmount;

  useEffect(() => {
    if (sortedOfferedProducts?.length) {
      setValue(
        ComplimentaryPeriodSectionField.PartnerProductId,
        sortedOfferedProducts[0].partnerProductId,
        { shouldValidate: true },
      );
    }
  }, [sortedOfferedProducts, setValue, cppLengthList]);

  useEffect(() => {
    if (baseDate) {
      setValue(ComplimentaryPeriodSectionField.CppExpirationDate, DEFAULT_CPP_LENGTH.value);
    }
  }, [DEFAULT_CPP_LENGTH, baseDate, cppLengthList, setValue]);

  const onLengthChange = useCallback(
    (daysAmount: number) => {
      const item = getCppOptionFromList(cppLengthList, daysAmount);
      if (!item) {
        return;
      }

      setCustomModeList(item.label === CUSTOM_LABEL ? cppLengthList : null);
      setValue(ComplimentaryPeriodSectionField.CppExpirationDate, item.value, {
        shouldValidate: true,
      });
    },
    [cppLengthList, setValue],
  );

  return (
    <Stack gap={2}>
      <FormSelect
        type="select"
        name={ComplimentaryPeriodSectionField.PartnerProductId}
        label="Service Level"
        required
        options={productOptions}
        disabled={isLoadingOfferedProducts}
        data-testid="complimentary-period-product"
      />
      <FormChipGroup
        label="Length"
        options={lengthOptions}
        value={selectedLength}
        onChange={onLengthChange}
      />
      <FormDatePicker
        type="datepicker"
        name={ComplimentaryPeriodSectionField.CppExpirationDate}
        label="CPP Expiration Date"
        required="Expiration date is required"
        rules={{
          validate: (value: Dayjs | null) =>
            isDateWithinAllowedBoundariesForCPP(value) ||
            'Expiration date must be between tomorrow and 2 years from now',
        }}
        showErrorText
        disabled={!isCustomDate}
        datePickerProps={{ disablePast: true, minDate: TOMORROW_DATE, maxDate: MAX_DATE }}
        data-testid="complimentary-period-expiration-date"
      />
    </Stack>
  );
};
